import { Link, useLocation, useNavigate } from "react-router-dom";
import { useEffect, useState } from 'react';

import StatusMessage from '../ui/StatusMessage';
import { useAuth } from "../../context/AuthContext";

const menuItems = [
  { label: "Dashboard", path: "/" },
  {
    label: "Supplier System",
    children: [
      { label: "Suppliers", path: "/suppliers" },
      { label: "Products", path: "/products" },
      { label: "Categories", path: "/categories" },
    ],
  },
  { label: "Users", path: "/users", adminOnly: true },
  { label: "Action Center", path: "/activity", authOnly: true },
  { label: "Settings", path: "/settings", adminOnly: true }
];

const formatRoleLabel = (role) =>
  role ? role.charAt(0).toUpperCase() + role.slice(1) : 'Staff';

function isActivePath(currentPath, itemPath) {
  if (itemPath === "/") {
    return currentPath === "/";
  }

  if (itemPath === "/suppliers") {
    return (
      currentPath === "/suppliers" ||
      currentPath.startsWith("/suppliers/") ||
      currentPath.startsWith("/supplier/")
    );
  }

  if (itemPath === '/products') {
    return currentPath === '/products' || currentPath.startsWith('/products/');
  }

  return currentPath === itemPath;
}

function canViewItem(item, { isAdmin, user }) {
  if (item.adminOnly) {
    return isAdmin;
  }

  if (item.authOnly) {
    return Boolean(user);
  }

  return true;
}

export default function Sidebar({ onNavigate = () => {} }) {
  const location = useLocation();
  const navigate = useNavigate();
  const [authError, setAuthError] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [expandedGroups, setExpandedGroups] = useState(() => ({
    "Supplier System": true,
  }));
  const {
    user,
    role,
    isAdmin,
    isLoading,
    loginWithEmailPassword,
    logout,
  } = useAuth();

  useEffect(() => {
    const activeGroupLabels = menuItems
      .filter((item) =>
        Array.isArray(item.children) &&
        item.children.some((child) => isActivePath(location.pathname, child.path))
      )
      .map((item) => item.label);

    if (activeGroupLabels.length === 0) {
      return;
    }

    setExpandedGroups((current) => {
      const next = { ...current };
      let hasChange = false;

      activeGroupLabels.forEach((label) => {
        if (!next[label]) {
          next[label] = true;
          hasChange = true;
        }
      });

      return hasChange ? next : current;
    });
  }, [location.pathname]);

  const handleEmailLogin = async (event) => {
    event.preventDefault();

    try {
      setAuthError('');
      await loginWithEmailPassword(email.trim(), password);
      setPassword('');
    } catch (error) {
      console.error("EMAIL LOGIN ERROR:", error);
      setAuthError('Invalid email or password.');
    }
  };

  const handleLogout = async () => {
    try {
      setAuthError('');
      await logout();
      navigate("/");
    } catch (error) {
      console.error("LOGOUT ERROR:", error);
      setAuthError('Failed to log out.');
    }
  };

  const toggleGroup = (label) => {
    setExpandedGroups((current) => ({
      ...current,
      [label]: !current[label],
    }));
  };

  return (
    <aside className="sidebar">
      <nav>
        {menuItems
          .filter((item) => canViewItem(item, { isAdmin, user }))
          .map((item) => {
            if (Array.isArray(item.children)) {
              const visibleChildren = item.children.filter((child) =>
                canViewItem(child, { isAdmin, user })
              );
              const isExpanded = Boolean(expandedGroups[item.label]);
              const hasActiveChild = visibleChildren.some((child) =>
                isActivePath(location.pathname, child.path)
              );

              if (visibleChildren.length === 0) {
                return null;
              }

              return (
                <div
                  key={item.label}
                  className={`sidebar-group${hasActiveChild ? " active" : ""}`}
                >
                  <button
                    type="button"
                    className="sidebar-group-toggle"
                    aria-expanded={isExpanded}
                    onClick={() => toggleGroup(item.label)}
                  >
                    <span>{item.label}</span>
                    <span className="sidebar-group-chevron" aria-hidden="true">
                      {isExpanded ? "v" : ">"}
                    </span>
                  </button>

                  {isExpanded ? (
                    <div className="sidebar-subnav">
                      {visibleChildren.map((child) => {
                        const isActive = isActivePath(location.pathname, child.path);

                        return (
                          <Link
                            key={child.path}
                            to={child.path}
                            onClick={onNavigate}
                            className={`sidebar-item sidebar-subitem${isActive ? " active" : ""}`}
                          >
                            {child.label}
                          </Link>
                        );
                      })}
                    </div>
                  ) : null}
                </div>
              );
            }

            const isActive = isActivePath(location.pathname, item.path);

            return (
              <Link
                key={item.path}
                to={item.path}
                onClick={onNavigate}
                className={`sidebar-item${isActive ? " active" : ""}`}
              >
                {item.label}
              </Link>
            );
          })}
      </nav>

      <div style={{ marginTop: 24, display: "grid", gap: 10 }}>
        <StatusMessage tone="error" message={authError} />
        {user ? (
          <>
            <div
              style={{
                padding: "10px 12px",
                borderRadius: 12,
                background: isAdmin ? "#ecfdf5" : "#f8fafc",
                color: isAdmin ? "#166534" : "#475569",
                fontSize: 13,
                lineHeight: 1.4,
              }}
            >
              <strong style={{ display: "block", marginBottom: 4 }}>
                role: {formatRoleLabel(role)}
              </strong>
              <span>{user.email}</span>
            </div>

            <button
              type="button"
              onClick={handleLogout}
              style={{
                border: "1px solid #cbd5e1",
                borderRadius: 12,
                padding: "10px 12px",
                background: "#ffffff",
                color: "#0f172a",
                fontSize: 14,
                fontWeight: 600,
                cursor: "pointer",
              }}
            >
              Logout
            </button>
          </>
        ) : (
          <>
            <form
              onSubmit={handleEmailLogin}
              style={{ display: 'grid', gap: 8 }}
            >
              <input
                type="email"
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                placeholder="Email"
                disabled={isLoading}
                required
                style={{
                  border: "1px solid #cbd5e1",
                  borderRadius: 10,
                  padding: "9px 10px",
                  fontSize: 13,
                  boxSizing: "border-box",
                  width: "100%",
                }}
              />
              <input
                type="password"
                value={password}
                onChange={(event) => setPassword(event.target.value)}
                placeholder="Password"
                disabled={isLoading}
                required
                style={{
                  border: "1px solid #cbd5e1",
                  borderRadius: 10,
                  padding: "9px 10px",
                  fontSize: 13,
                  boxSizing: "border-box",
                  width: "100%",
                }}
              />
              <button
                type="submit"
                disabled={isLoading || !email.trim() || !password}
                style={{
                  border: "1px solid #cbd5e1",
                  borderRadius: 12,
                  padding: "10px 12px",
                  background: "#ffffff",
                  color: isLoading ? "#94a3b8" : "#0f172a",
                  fontSize: 14,
                  fontWeight: 600,
                  cursor: isLoading ? "not-allowed" : "pointer",
                }}
              >
                {isLoading ? "Checking access..." : "Login"}
              </button>
            </form>
          </>
        )}
      </div>
    </aside>
  );
}
